import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Power, AlertTriangle } from "lucide-react";
import { Progress } from "@shared/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import { useMonthlySupportSummary } from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface DisconnectionSummaryProps {
  timeRange: "day" | "week" | "month" | "quarter" | "year";
  userRole: "manager" | "executive" | "callcenter" | "recovery";
}

interface DisconnectionCategoryProps {
  category: string;
  count: number;
  percentage: number;
  trend: "up" | "down" | "neutral";
}

function DisconnectionCategory({ category, count, percentage, trend }: DisconnectionCategoryProps) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-[10px] font-medium truncate">{category}</span>
        <div className="flex items-center gap-1">
          <span className="text-[10px]">{count}</span>
          <span className={`text-[10px] ${
            trend === 'up' ? 'text-red-500' : 
            trend === 'down' ? 'text-green-500' : 'text-gray-500'
          }`}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
          </span>
        </div>
      </div>
      <Progress value={percentage} className="h-1" />
    </div>
  );
}

export function DisconnectionSummary({ timeRange, userRole }: DisconnectionSummaryProps) {
      const remoteUtilityId = getRemoteUtilityId();
  const { 
    data: supportData, 
    isLoading: isSupportLoading, 
    error: supportError 
  } = useMonthlySupportSummary({
    remote_utility_id: remoteUtilityId,
  });

  // Transform disconnection categories from API response (top 5 only)
  function transformDisconnectionCategories(disconnectionTypes: Record<string, number>) {
    const entries = Object.entries(disconnectionTypes);
    const totalCount = entries.reduce((sum, [, count]) => sum + count, 0);
    
    // Sort by count and take top 5
    const sortedEntries = entries
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return sortedEntries.map(([category, count]) => ({
      category: category.length > 20 ? category.substring(0, 17) + "..." : category,
      count,
      percentage: totalCount > 0 ? Math.round((count / totalCount) * 100) : 0,
      trend: "neutral" as const
    }));
  }

  // Transform status data from API response
  function transformStatusData(statusCounts: Record<string, number>) {
    const totalCount = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
    
    if (totalCount === 0) {
      return [
        { level: "Pending", value: 35 },
        { level: "Scheduled", value: 30 },
        { level: "In Progress", value: 20 },
        { level: "Completed", value: 15 }
      ];
    }

    const pending = statusCounts["created"] || 0;
    const scheduled = 0; // Not in API, keep as 0
    const inProgress = statusCounts["in_progress"] || statusCounts["approved"] || 0;
    const completed = statusCounts["completed"] || 0;

    return [
      { level: "Pending", value: Math.round((pending / totalCount) * 100) },
      { level: "Scheduled", value: 0 },
      { level: "In Progress", value: Math.round((inProgress / totalCount) * 100) },
      { level: "Completed", value: Math.round((completed / totalCount) * 100) }
    ];
  }

  // Transform API data to disconnection data
  const disconnectionData = supportData?.result ? {
    pendingDisconnections: supportData.result.disconnections.active,
    completedToday: supportData.result.disconnections.resolvedToday,
    reconnectionsToday: supportData.result.reconnections.active,
    averageProcessTime: `${(supportData.result.disconnections.avgCompletionTimeHr / 24).toFixed(1)} days`,

    status: transformStatusData(supportData.result.disconnections.statusCounts || {})
  } : {
    // Fallback data if API is not available
    pendingDisconnections: 24,
    completedToday: 12,
    reconnectionsToday: 8,
    averageProcessTime: "3.2 days",
    categories: [
      { category: "Non-Payment", count: 18, percentage: 75, trend: "up" as const },
      { category: "Violation", count: 4, percentage: 17, trend: "neutral" as const },
      { category: "Maintenance", count: 2, percentage: 8, trend: "down" as const },
    ],
    status: [
      { level: "Pending", value: 35 },
      { level: "Scheduled", value: 30 },
      { level: "In Progress", value: 20 },
      { level: "Completed", value: 15 }
    ]
  };

  // Loading state - show original fallback data
  if (isSupportLoading) {
    const disconnectionData = {
      pendingDisconnections: 24,
      completedToday: 12,
      reconnectionsToday: 8,
      averageProcessTime: "3.2 days",
      categories: [
        { category: "Non-Payment", count: 18, percentage: 75, trend: "up" as const },
        { category: "Violation", count: 4, percentage: 17, trend: "neutral" as const },
        { category: "Maintenance", count: 2, percentage: 8, trend: "down" as const },
      ],
      status: [
        { level: "Pending", value: 35 },
        { level: "Scheduled", value: 30 },
        { level: "In Progress", value: 20 },
        { level: "Completed", value: 15 }
      ]
    };
  }

  // Error state - show original fallback data
  if (supportError) {
    const disconnectionData = {
      pendingDisconnections: 24,
      completedToday: 12,
      reconnectionsToday: 8,
      averageProcessTime: "3.2 days",
      categories: [
        { category: "Non-Payment", count: 18, percentage: 75, trend: "up" as const },
        { category: "Violation", count: 4, percentage: 17, trend: "neutral" as const },
        { category: "Maintenance", count: 2, percentage: 8, trend: "down" as const },
      ],
      status: [
        { level: "Pending", value: 35 },
        { level: "Scheduled", value: 30 },
        { level: "In Progress", value: 20 },
        { level: "Completed", value: 15 }
      ]
    };
  }

  return (
   <Card className="h-full">
  <CardHeader>
    <CardTitle className="card-heading-text flex items-center justify-between">
      Disconnection
      <Power className="h-4 w-4 text-muted-foreground" />
    </CardTitle>
  </CardHeader>
  <CardContent className="pt-2 space-y-3">
    {/* Pending & Completed Today */}
    <div className="grid grid-cols-2 gap-2">
      <div className="flex flex-col items-center justify-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">Pending</span>
        <span className="card-value-text font-bold text-red-600">
          {disconnectionData.pendingDisconnections}
        </span>
      </div>
      <div className="flex flex-col items-center justify-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">Completed Today</span>
        <span className="card-value-text font-bold text-green-600">
          {disconnectionData.completedToday}
        </span>
      </div>
    </div>

    {/* Reconnections & Avg Process Time */}
    <div className="grid grid-cols-2 gap-2">
      <div className="flex flex-col items-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">Reconnections</span>
        <span className="card-value-text font-bold">
          {disconnectionData.reconnectionsToday}
        </span>
      </div>
      <div className="flex flex-col items-center p-1.5 rounded border">
        <span className="card-subheading-text text-muted-foreground">Avg Process Time</span>
        <span className="card-value-text font-bold">
          {disconnectionData.averageProcessTime}
        </span>
      </div>
    </div>

    {/* By Reason */}
    <div className="space-y-1.5">
      <h3 className="card-subheading-text">By Reason</h3>
      <div className="space-y-2">
        {disconnectionData.categories?.map((category) => (
          <DisconnectionCategory key={category.category} {...category} />
        ))}
      </div>
    </div>

    {/* By Status */}
    <div className="space-y-1">
      <h3 className="card-subheading-text">By Status</h3>
      <TooltipProvider>
        <div className="flex items-center gap-0.5 h-3">
          {disconnectionData.status.map((level, index) => (
            <Tooltip key={level.level}>
              <TooltipTrigger asChild>
                <div
                  style={{ width: `${level.value}%` }}
                  className={`h-full rounded ${
                    index === 0
                      ? "bg-amber-500"
                      : index === 1
                      ? "bg-blue-500"
                      : index === 2
                      ? "bg-orange-400"
                      : "bg-green-400"
                  }`}
                />
              </TooltipTrigger>
              <TooltipContent className="card-value-text" side="top">
                {level.level}: {level.value}%
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>Pending</span>
        <span>Scheduled</span>
        <span>In Progress</span>
        <span>Completed</span>
      </div>
    </div>
  </CardContent>
</Card>

  );
}